import { applyDecorators } from '@nestjs/common';
import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { ApiPropertyOptions } from '@nestjs/swagger/dist/decorators/api-property.decorator';
import { Transform } from 'class-transformer';
import { IsOptional, registerDecorator, ValidationOptions } from 'class-validator';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

import { INVALID_PHONE_NUMBER } from '../constants';
import { BaseDecoratorOptions } from '../types';

type PhoneNumberValidationOptions = {
  allowedCountries?: CountryCode[];
}

export function isValidPhoneNumber(value: string, options: PhoneNumberValidationOptions): boolean {
  const parsedNumber = parsePhoneNumberFromString(value);

  const { allowedCountries } = options;

  return !!parsedNumber?.isValid() &&
    (!allowedCountries || allowedCountries.includes(parsedNumber?.country!));
}

export function IsValidPhoneNumber(validationOptions?: ValidationOptions & PhoneNumberValidationOptions): PropertyDecorator {
  return function (object, propertyName: string | symbol) {
    registerDecorator({
      name: 'IsValidPhoneNumber',
      target: object.constructor,
      propertyName: propertyName.toString(),
      constraints: [],
      options: validationOptions,
      validator: {
        validate(value: any) {
          if (!value || typeof value !== 'string') {
            return false;
          }
          return isValidPhoneNumber(value, { allowedCountries: validationOptions?.allowedCountries });
        },
      },
    });
  };
}

type PropertyPhoneNumberParams = BaseDecoratorOptions<string> & PhoneNumberValidationOptions;

export function ApiPropertyPhoneNumber({
                                         isOptional,
                                         allowedCountries,
                                         ...options
                                       }: PropertyPhoneNumberParams = {}): PropertyDecorator {
  const propertyOptions: ApiPropertyOptions = {
    type: String,
    example: '+37533292821320',
    description: 'Phone number',
    ...options,
  };

  return applyDecorators(
    ...(isOptional ? [IsOptional(), ApiPropertyOptional(propertyOptions)] : [ApiProperty(propertyOptions)]),
    IsValidPhoneNumber({ message: INVALID_PHONE_NUMBER, allowedCountries }),
    Transform(({ value }: { value: unknown }): string | unknown => {
      if (typeof value === 'string' && value) {
        return parsePhoneNumberFromString(value)!.format('E.164');
      }
      return value;
    }),
  );
}
